<script setup>
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import NextButton from 'dashboard/components-next/button/Button.vue';
import { aiAssistantConfigAPI } from 'dashboard/api/aiAssistant';

const props = defineProps({
  inbox: {
    type: Object,
    required: true,
  },
});

const { t } = useI18n();

const MODES = ['disabled', 'suggestion', 'auto'];

const isLoading = ref(true);
const isSaving = ref(false);
const providers = ref([]);
const placeholders = ref([]);
const languages = ref([]);
const defaultPrompt = ref('');
const encryptionEnabled = ref(true);
const apiKeyPresent = ref(false);
const maskedApiKey = ref('');
const apiKeyInput = ref('');
const showAdvanced = ref(false);

const form = ref({
  mode: 'disabled',
  provider: 'deepseek',
  model: 'deepseek-v4-flash',
  prompt: '',
  temperature: 0.7,
  max_tokens: 1000,
  request_timeout: 20,
  history_limit: 10,
  default_language: 'pt_BR',
  allow_markdown: false,
  allow_emojis: true,
  reply_delay: 10,
  split_messages: true,
  max_message_length: 300,
  payment_command_enabled: false,
  payment_command_template: '/pay {valor}',
});

const availableModels = computed(() => {
  const provider = providers.value.find(p => p.value === form.value.provider);
  return provider?.models || [];
});

const applyConfig = data => {
  const config = data.config || {};
  providers.value = data.providers || [];
  placeholders.value = data.placeholders || [];
  languages.value = data.languages || [];
  defaultPrompt.value = data.default_prompt || '';
  encryptionEnabled.value = Boolean(data.encryption_enabled);
  apiKeyPresent.value = Boolean(config.api_key_present);
  maskedApiKey.value = config.masked_api_key || '';

  form.value = {
    mode: config.mode || 'disabled',
    provider: config.provider || 'deepseek',
    model: config.model || 'deepseek-v4-flash',
    prompt: config.prompt || '',
    temperature: config.temperature ?? 0.7,
    max_tokens: config.max_tokens ?? 1000,
    request_timeout: config.request_timeout ?? 20,
    history_limit: config.history_limit ?? 10,
    default_language: config.default_language || 'pt_BR',
    allow_markdown: Boolean(config.allow_markdown),
    allow_emojis: config.allow_emojis !== false,
    reply_delay: config.reply_delay ?? 10,
    split_messages: config.split_messages !== false,
    max_message_length: config.max_message_length ?? 300,
    payment_command_enabled: Boolean(config.payment_command_enabled),
    payment_command_template: config.payment_command_template || '/pay {valor}',
  };
};

const fetchConfig = async () => {
  isLoading.value = true;
  try {
    const { data } = await aiAssistantConfigAPI.get(props.inbox.id);
    applyConfig(data);
  } catch (error) {
    useAlert(t('AI_ASSISTANT.SETTINGS.LOAD_ERROR'));
  } finally {
    isLoading.value = false;
  }
};

const save = async () => {
  isSaving.value = true;
  try {
    const payload = { ...form.value };
    if (apiKeyInput.value.trim()) payload.api_key = apiKeyInput.value.trim();

    const { data } = await aiAssistantConfigAPI.update(props.inbox.id, payload);
    applyConfig(data);
    apiKeyInput.value = '';
    useAlert(t('AI_ASSISTANT.SETTINGS.SAVED'));
  } catch (error) {
    const errors = error?.response?.data?.errors;
    useAlert(
      Array.isArray(errors) && errors.length
        ? errors.join(', ')
        : t('AI_ASSISTANT.SETTINGS.SAVE_ERROR')
    );
  } finally {
    isSaving.value = false;
  }
};

// built in JS: a literal {{name}} inside a Vue template would close the
// interpolation early
const placeholderLabel = name => `{{${name}}}`;

const resetPrompt = () => {
  form.value.prompt = defaultPrompt.value;
};

const onProviderChange = () => {
  const provider = providers.value.find(p => p.value === form.value.provider);
  if (provider?.default_model) form.value.model = provider.default_model;
};

onMounted(fetchConfig);
</script>

<template>
  <div class="flex flex-col gap-6 p-1">
    <div class="flex flex-col gap-1">
      <h2 class="text-base font-medium text-n-slate-12">
        {{ $t('AI_ASSISTANT.SETTINGS.TITLE') }}
      </h2>
      <p class="text-sm text-n-slate-11">
        {{ $t('AI_ASSISTANT.SETTINGS.DESCRIPTION') }}
      </p>
    </div>

    <div v-if="isLoading" class="flex items-center gap-2 text-sm text-n-slate-11">
      <span class="i-lucide-loader-circle size-4 animate-spin" />
      {{ $t('AI_ASSISTANT.PANEL.GENERATING') }}
    </div>

    <template v-else>
      <!-- mode -->
      <section class="flex flex-col gap-3">
        <span class="text-sm font-medium text-n-slate-12">
          {{ $t('AI_ASSISTANT.SETTINGS.MODE.LABEL') }}
        </span>
        <label
          v-for="option in MODES"
          :key="option"
          class="flex items-start gap-3 rounded-lg border p-3 cursor-pointer transition-colors"
          :class="
            form.mode === option
              ? 'border-n-brand bg-n-alpha-1'
              : 'border-n-weak hover:bg-n-alpha-1'
          "
        >
          <input
            v-model="form.mode"
            type="radio"
            :value="option"
            class="mt-1"
          />
          <span class="flex flex-col gap-0.5">
            <span class="text-sm font-medium text-n-slate-12">
              {{ $t(`AI_ASSISTANT.SETTINGS.MODE.${option.toUpperCase()}`) }}
            </span>
            <span class="text-xs text-n-slate-11">
              {{ $t(`AI_ASSISTANT.SETTINGS.MODE.${option.toUpperCase()}_HINT`) }}
            </span>
          </span>
        </label>
      </section>

      <!-- provider -->
      <section class="grid gap-4 sm:grid-cols-2">
        <label class="flex flex-col gap-1">
          <span class="text-sm font-medium text-n-slate-12">
            {{ $t('AI_ASSISTANT.SETTINGS.PROVIDER') }}
          </span>
          <select
            v-model="form.provider"
            class="rounded-lg border border-n-weak bg-n-solid-1 px-3 py-2 text-sm text-n-slate-12"
            @change="onProviderChange"
          >
            <option v-for="p in providers" :key="p.value" :value="p.value">
              {{ p.label }}
            </option>
          </select>
        </label>
        <label class="flex flex-col gap-1">
          <span class="text-sm font-medium text-n-slate-12">
            {{ $t('AI_ASSISTANT.SETTINGS.MODEL') }}
          </span>
          <select
            v-model="form.model"
            class="rounded-lg border border-n-weak bg-n-solid-1 px-3 py-2 text-sm text-n-slate-12"
          >
            <option v-for="m in availableModels" :key="m" :value="m">
              {{ m }}
            </option>
          </select>
        </label>
      </section>

      <!-- api key -->
      <section class="flex flex-col gap-1">
        <span class="text-sm font-medium text-n-slate-12">
          {{ $t('AI_ASSISTANT.SETTINGS.API_KEY') }}
        </span>
        <input
          v-model="apiKeyInput"
          type="password"
          autocomplete="off"
          :placeholder="
            apiKeyPresent
              ? maskedApiKey
              : $t('AI_ASSISTANT.SETTINGS.API_KEY_PLACEHOLDER')
          "
          class="rounded-lg border border-n-weak bg-n-solid-1 px-3 py-2 text-sm text-n-slate-12"
        />
        <span v-if="apiKeyPresent" class="text-xs text-n-slate-11">
          {{ $t('AI_ASSISTANT.SETTINGS.API_KEY_STORED') }}
        </span>
        <span
          class="text-xs"
          :class="encryptionEnabled ? 'text-n-slate-11' : 'text-n-ruby-11'"
        >
          {{
            encryptionEnabled
              ? $t('AI_ASSISTANT.SETTINGS.API_KEY_ENCRYPTED')
              : $t('AI_ASSISTANT.SETTINGS.API_KEY_PLAIN')
          }}
        </span>
      </section>

      <!-- prompt -->
      <section class="flex flex-col gap-1">
        <div class="flex items-center justify-between gap-2">
          <span class="text-sm font-medium text-n-slate-12">
            {{ $t('AI_ASSISTANT.SETTINGS.PROMPT') }}
          </span>
          <NextButton
            slate
            link
            xs
            :label="$t('AI_ASSISTANT.SETTINGS.PROMPT_RESET')"
            @click="resetPrompt"
          />
        </div>
        <textarea
          v-model="form.prompt"
          rows="8"
          :placeholder="defaultPrompt"
          class="w-full resize-y rounded-lg border border-n-weak bg-n-solid-1 px-3 py-2 text-sm font-mono text-n-slate-12"
        />
        <span class="text-xs text-n-slate-11">
          {{ $t('AI_ASSISTANT.SETTINGS.PROMPT_HINT') }}
          <code
            v-for="ph in placeholders"
            :key="ph"
            class="mx-0.5 rounded bg-n-alpha-2 px-1"
          >
            {{ placeholderLabel(ph) }}
          </code>
        </span>
      </section>

      <!-- toggles -->
      <section class="grid gap-4 sm:grid-cols-2">
        <label class="flex flex-col gap-1">
          <span class="text-sm font-medium text-n-slate-12">
            {{ $t('AI_ASSISTANT.SETTINGS.LANGUAGE') }}
          </span>
          <select
            v-model="form.default_language"
            class="rounded-lg border border-n-weak bg-n-solid-1 px-3 py-2 text-sm text-n-slate-12"
          >
            <option v-for="lang in languages" :key="lang" :value="lang">
              {{ lang }}
            </option>
          </select>
        </label>
        <label class="flex flex-col gap-1">
          <span class="text-sm font-medium text-n-slate-12">
            {{ $t('AI_ASSISTANT.SETTINGS.HISTORY_LIMIT') }}
          </span>
          <input
            v-model.number="form.history_limit"
            type="number"
            min="0"
            max="50"
            class="rounded-lg border border-n-weak bg-n-solid-1 px-3 py-2 text-sm text-n-slate-12"
          />
        </label>
        <label class="flex items-center gap-2">
          <input v-model="form.allow_markdown" type="checkbox" />
          <span class="text-sm text-n-slate-12">
            {{ $t('AI_ASSISTANT.SETTINGS.ALLOW_MARKDOWN') }}
          </span>
        </label>
        <label class="flex items-center gap-2">
          <input v-model="form.allow_emojis" type="checkbox" />
          <span class="text-sm text-n-slate-12">
            {{ $t('AI_ASSISTANT.SETTINGS.ALLOW_EMOJIS') }}
          </span>
        </label>
      </section>

      <!-- pacing: only meaningful when the assistant answers on its own -->
      <section v-if="form.mode === 'auto'" class="flex flex-col gap-3">
        <div class="flex flex-col gap-0.5">
          <span class="text-sm font-medium text-n-slate-12">
            {{ $t('AI_ASSISTANT.SETTINGS.PACING') }}
          </span>
          <span class="text-xs text-n-slate-11">
            {{ $t('AI_ASSISTANT.SETTINGS.PACING_HINT') }}
          </span>
        </div>
        <div class="grid gap-4 sm:grid-cols-2">
          <label class="flex flex-col gap-1">
            <span class="text-sm font-medium text-n-slate-12">
              {{ $t('AI_ASSISTANT.SETTINGS.REPLY_DELAY') }}
            </span>
            <input
              v-model.number="form.reply_delay"
              type="number"
              min="0"
              max="600"
              class="rounded-lg border border-n-weak bg-n-solid-1 px-3 py-2 text-sm text-n-slate-12"
            />
            <span class="text-xs text-n-slate-11">
              {{ $t('AI_ASSISTANT.SETTINGS.REPLY_DELAY_HINT') }}
            </span>
          </label>
          <label class="flex flex-col gap-1">
            <span class="text-sm font-medium text-n-slate-12">
              {{ $t('AI_ASSISTANT.SETTINGS.MAX_MESSAGE_LENGTH') }}
            </span>
            <input
              v-model.number="form.max_message_length"
              type="number"
              min="60"
              max="2000"
              :disabled="!form.split_messages"
              class="rounded-lg border border-n-weak bg-n-solid-1 px-3 py-2 text-sm text-n-slate-12 disabled:opacity-50"
            />
            <span class="text-xs text-n-slate-11">
              {{ $t('AI_ASSISTANT.SETTINGS.MAX_MESSAGE_LENGTH_HINT') }}
            </span>
          </label>
          <label class="flex items-center gap-2">
            <input v-model="form.split_messages" type="checkbox" />
            <span class="text-sm text-n-slate-12">
              {{ $t('AI_ASSISTANT.SETTINGS.SPLIT_MESSAGES') }}
            </span>
          </label>
        </div>

        <div class="rounded-lg border border-n-weak p-3 flex flex-col gap-2">
          <label class="flex items-center gap-2">
            <input v-model="form.payment_command_enabled" type="checkbox" />
            <span class="text-sm font-medium text-n-slate-12">
              {{ $t('AI_ASSISTANT.SETTINGS.PAYMENT_COMMAND') }}
            </span>
          </label>
          <span class="text-xs text-n-slate-11">
            {{ $t('AI_ASSISTANT.SETTINGS.PAYMENT_COMMAND_HINT') }}
          </span>
          <label v-if="form.payment_command_enabled" class="flex flex-col gap-1">
            <span class="text-sm text-n-slate-11">
              {{ $t('AI_ASSISTANT.SETTINGS.PAYMENT_TEMPLATE') }}
            </span>
            <input
              v-model="form.payment_command_template"
              class="rounded-lg border border-n-weak bg-n-solid-1 px-3 py-2 text-sm font-mono text-n-slate-12"
            />
            <span class="text-xs text-n-slate-11">
              {{ $t('AI_ASSISTANT.SETTINGS.PAYMENT_TEMPLATE_HINT') }}
            </span>
          </label>
        </div>
      </section>

      <!-- advanced -->
      <section class="flex flex-col gap-3">
        <NextButton
          slate
          faded
          sm
          class="w-fit"
          :icon="showAdvanced ? 'i-lucide-chevron-up' : 'i-lucide-chevron-down'"
          :label="$t('AI_ASSISTANT.SETTINGS.ADVANCED')"
          @click="showAdvanced = !showAdvanced"
        />
        <div v-if="showAdvanced" class="grid gap-4 sm:grid-cols-2">
          <label class="flex flex-col gap-1">
            <span class="text-sm font-medium text-n-slate-12">
              {{ $t('AI_ASSISTANT.SETTINGS.TEMPERATURE') }}
            </span>
            <input
              v-model.number="form.temperature"
              type="number"
              step="0.1"
              min="0"
              max="2"
              class="rounded-lg border border-n-weak bg-n-solid-1 px-3 py-2 text-sm text-n-slate-12"
            />
          </label>
          <label class="flex flex-col gap-1">
            <span class="text-sm font-medium text-n-slate-12">
              {{ $t('AI_ASSISTANT.SETTINGS.MAX_TOKENS') }}
            </span>
            <input
              v-model.number="form.max_tokens"
              type="number"
              min="1"
              max="8000"
              class="rounded-lg border border-n-weak bg-n-solid-1 px-3 py-2 text-sm text-n-slate-12"
            />
          </label>
          <label class="flex flex-col gap-1">
            <span class="text-sm font-medium text-n-slate-12">
              {{ $t('AI_ASSISTANT.SETTINGS.TIMEOUT') }}
            </span>
            <input
              v-model.number="form.request_timeout"
              type="number"
              min="1"
              max="120"
              class="rounded-lg border border-n-weak bg-n-solid-1 px-3 py-2 text-sm text-n-slate-12"
            />
          </label>
        </div>
      </section>

      <div class="flex justify-start">
        <NextButton
          blue
          sm
          :is-loading="isSaving"
          :disabled="isSaving"
          :label="$t('AI_ASSISTANT.SETTINGS.SAVE')"
          @click="save"
        />
      </div>
    </template>
  </div>
</template>
